<template>
  <v-dialog
    v-model="add_resource_dialog"
    max-width="600px"
    style="max-width: 1200px !important; max-height: 1000px !important">
    <AddResource
      :selected="selected"
      v-if="add_resource_dialog"
      v-on:close="add_resource_dialog = false" />
  </v-dialog>
  <div style="display: flex">
    <div style="font-weight: bold; font-size: 20px">
      <v-icon class="mr-3">mdi-folder-multiple-image</v-icon>
      Assets
    </div>
    <v-spacer />
    <HelpButton mdx-name="AssetsConfig" />
  </div>
  <v-divider class="my-3" />
  <div class="config-page-body">
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>General Asset Settings</v-card-subtitle>
      <v-divider />
    </div>
    <v-text-field
      class="mb-5"
      @click="editing_assets_location = true"
      v-on:keyup.enter="save_location()"
      filled
      label="Media Storage Location"
      hint="This is the location CanvasPro will use to store any media that has been uploaded"
      persistent-hint
      v-model="location"></v-text-field>
    <v-text-field
      filled
      v-model="asset_expire_time"
      hint="Note: If an asset is still in use, it will not be removed even if is is expired "
      persistent-hint
      label="Default Days Until Asset Expire"></v-text-field>
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>Ingest Locations</v-card-subtitle>
      <v-divider />
    </div>
    <div class="ma-0 pa-0 ingest-parent-progress">
      <v-progress-linear
        height="8"
        dark
        class="ingest-progress-bar"
        v-if="deluge && deluge.ingest && deluge.ingest.filename"
        v-model="deluge.ingest.progress">
        <strong>
          {{ deluge && deluge.ingest ? deluge.ingest.filename : '' }}
          {{ deluge && deluge.ingest ? deluge.ingest.assetcount : '' }} /
          {{ deluge && deluge.ingest ? deluge.ingest.allfiles : '' }}
        </strong>
      </v-progress-linear>
    </div>
    <v-card
      variant="flat"
      flat
      v-for="(rl, i) in deluge.config.resource_locations"
      :key="(rl, i)">
      <v-card-title style="padding: 1rem !important">
        <div class="ingest-header">
          <span
            class="v-card-title"
            style="padding: 0px">
            {{ rl.name }}
          </span>
          <v-card-subtitle>
            <v-chip
              v-for="(tag, i) in rl.tags"
              :key="(tag, i)"
              dark
              class="mr-1 mb-2"
              :color="deluge.tags[tag].color">
              {{ deluge.tags[tag].name }}
            </v-chip>
          </v-card-subtitle>
        </div>

        <v-spacer />
        <v-card-subtitle style="padding: 0px">
          <p>{{ rl.data.location }}</p>

          <div v-if="rl.last_modified != 0">
            Status: Synced
            <br />
            Folder Last Modified:
            {{ utcSecondsToLocaleDate(rl.last_modified) }} ({{
              utcSecondsToRelative(rl.last_modified)
            }})
          </div>
          <div v-else>Status: Syncing</div>
        </v-card-subtitle>
        <div style="display: flex; justify-content: left; margin-top: 20px">
          <v-btn
            @click="
              selected = rl;
              add_resource_dialog = true;
            "
            color="primary"
            dark
            variant="tonal"
            class="mr-2">
            edit
          </v-btn>
          <v-btn
            @click="delete_resource(rl.id)"
            color="red"
            dark
            class="mr-2"
            variant="tonal">
            Remove
          </v-btn>
          <v-btn
            variant="tonal"
            color="primary"
            class="sync-button"
            dark
            title="Force a sync of this Ingest Location"
            @click="resetResourceLocation(rl.uid)">
            <v-icon>mdi-sync</v-icon>
          </v-btn>
        </div>
      </v-card-title>
    </v-card>
    <v-card-actions class="ma-0 pa-0 mt-2">
      <v-btn
        color="primary"
        variant="tonal"
        block
        @click="
          selected = undefined;
          add_resource_dialog = true;
        ">
        Add Ingest Location
      </v-btn>
    </v-card-actions>
    <v-divider class="my-3" />
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>Asset Optimization Status</v-card-subtitle>
      <v-divider />
    </div>

    <!-- Conversion Status Display -->
    <v-alert
      v-if="deluge?.asset_conversion_status?.status === 'converting'"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-3"
      prominent
      icon="mdi-lightning-bolt">
      <div style="display: flex; align-items: center; gap: 12px">
        <v-progress-circular
          indeterminate
          size="20"
          width="2"
          color="primary" />
        <div>
          <div class="text-subtitle-2">
            Converting:
            <strong>{{ deluge.asset_conversion_status.asset_name }}</strong>
          </div>
          <div class="text-caption">
            Target format: {{ deluge.asset_conversion_status.target_format }}
          </div>
        </div>
      </div>
    </v-alert>

    <v-alert
      v-else
      type="success"
      variant="tonal"
      density="compact"
      class="mb-3"
      icon="mdi-check-circle">
      No conversions currently in progress
    </v-alert>

    <v-divider class="my-3" />
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>Auto Conversion Rules</v-card-subtitle>
      <v-divider />
    </div>
    <div class="conversion-rules-container">
      <p class="text-caption mb-3">
        Automatically convert assets for optimization based on tags.
      </p>

      <v-card
        v-for="(rule, index) in conversion_rules"
        :key="index"
        variant="outlined"
        class="mb-3 pa-3 conversion-rule-card">
        <div
          style="
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 12px;
          ">
          <v-switch
            v-model="rule.enabled"
            density="compact"
            hide-details
            color="primary"
            style="flex: 0 0 auto" />

          <v-select
            v-model="rule.tag_name"
            :items="available_tags"
            label="If asset has tag"
            density="comfortable"
            hide-details
            style="flex: 1" />

          <v-select
            v-model="rule.target_format"
            :items="['DDS', 'HAP']"
            label="Convert to"
            density="comfortable"
            hide-details
            style="flex: 1"
            @update:model-value="onTargetFormatChange(rule)" />

          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="remove_conversion_rule(index)" />
        </div>

        <!-- Resolution Scaling Options -->
        <v-divider class="my-2" />
        <div style="display: flex; align-items: center; gap: 16px">
          <v-select
            v-model="rule.scale_mode"
            :items="[
              { title: 'Downscale only (default)', value: 'downscale_only' },
              { title: 'Upscale only', value: 'upscale_only' },
              { title: 'Both', value: 'both' },
              { title: 'No scaling', value: 'none' },
            ]"
            label="Scaling mode"
            density="comfortable"
            hide-details
            style="flex: 2" />

          <v-text-field
            v-model.number="rule.max_width"
            label="Max width"
            type="number"
            density="comfortable"
            hide-details
            :disabled="rule.scale_mode === 'none'"
            style="flex: 1" />

          <v-text-field
            v-model.number="rule.max_height"
            label="Max height"
            type="number"
            density="comfortable"
            hide-details
            :disabled="rule.scale_mode === 'none'"
            style="flex: 1" />
        </div>

        <!-- Framerate Option (HAP only) -->
        <div
          v-if="rule.target_format === 'HAP'"
          style="
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 12px;
          ">
          <v-text-field
            v-model.number="rule.target_framerate"
            label="Target framerate (optional)"
            type="number"
            density="comfortable"
            hide-details
            clearable
            placeholder="Leave empty to keep original"
            hint="Force specific framerate (e.g., 30, 60)"
            persistent-hint
            style="flex: 1" />
        </div>
      </v-card>

      <v-btn
        color="primary"
        variant="tonal"
        block
        @click="add_conversion_rule"
        prepend-icon="mdi-plus">
        Add Conversion Rule
      </v-btn>
    </div>
    <v-divider class="my-3" />
    <div style="display: flex">
      <div style="font-weight: bold; font-size: 20px"></div>
      <v-spacer />
      <v-btn
        :loading="cleanAssetsLoading"
        title="Remove all expired or soft deleted assets permanently"
        color="primary"
        @click="clean_assets()"
        class="mr-3"
        variant="flat">
        <v-icon class="ml-1 mr-2">mdi-delete-forever</v-icon>
        Clean
      </v-btn>
      <v-btn
        color="primary"
        :loading="loadingSave"
        @click="save_location()"
        variant="flat">
        <v-icon class="ml-1 mr-2">mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>
<script setup>
import { deluge } from '@/stores/deluge.js';
import {
  utcSecondsToLocaleDate,
  utcSecondsToRelative,
} from '@/utils/dateTimeUtils.js';
import { onMounted, ref, computed } from 'vue';
import { $http } from '../../stores/http.js';
import HelpButton from '../Help-Hints/HelpButton.vue';
import AddResource from './add_location.vue';
const loadingSave = ref(false);
const editing_assets_location = ref(false);
const location = ref(null);
const asset_expire_time = ref(0);

const cleanAssetsLoading = ref(false);
const add_resource_dialog = ref(false);
const selected = ref(undefined);

// Conversion rules
const conversion_rules = ref([]);

// Use canvas size as default max — assets should never exceed the canvas
const defaultMaxWidth = computed(() => deluge?.config?.canvas?.x || 3840);
const defaultMaxHeight = computed(() => deluge?.config?.canvas?.y || 2160);

const available_tags = computed(() => {
  if (!deluge || !deluge.tags) return [];
  // Filter to only show Image and Video tags
  return Object.values(deluge.tags)
    .filter((tag) => tag.name === 'Image' || tag.name === 'Video')
    .map((tag) => tag.name);
});

function onTargetFormatChange(rule) {
  // Clear framerate when switching away from HAP
  if (rule.target_format !== 'HAP') {
    rule.target_framerate = null;
  }
}

function delete_resource(rl_id) {
  $http(
    'DELETE',
    '/delete_resource_location?resource_location_id=' + rl_id
  ).then(() => {});
}

onMounted(() => {
  set_location();
  load_conversion_rules();
});

function set_location() {
  if (!editing_assets_location.value) {
    if (deluge?.config?.asset_expire_time) {
      asset_expire_time.value = deluge.config.asset_expire_time;
      if ('assets_location' in deluge['config']) {
        if (deluge['config']['assets_location']) {
          location.value = deluge['config']['assets_location'];
        }
      }
    }
  }
}

async function load_conversion_rules() {
  try {
    const response_raw = await $http('GET', '/get_asset_conversion_rules');
    const response = JSON.parse(response_raw);
    conversion_rules.value = response.rules || [];

    // Add default rule if empty and save it
    if (conversion_rules.value.length === 0) {
      conversion_rules.value.push({
        tag_name: 'Image',
        target_format: 'DDS',
        enabled: false,
        scale_mode: 'downscale_only',
        max_width: defaultMaxWidth.value,
        max_height: defaultMaxHeight.value,
      });
      // Auto-save the default rule
      await save_conversion_rules();
      console.log('Initialized default conversion rule: Image -> DDS');
    }
  } catch (e) {
    console.error('Failed to load conversion rules:', e);
  }
}

function add_conversion_rule() {
  conversion_rules.value.push({
    tag_name: '',
    target_format: 'DDS',
    enabled: true,
    scale_mode: 'downscale_only',
    max_width: defaultMaxWidth.value,
    max_height: defaultMaxHeight.value,
    target_framerate: null,
  });
}

function remove_conversion_rule(index) {
  conversion_rules.value.splice(index, 1);
}

async function save_conversion_rules() {
  try {
    console.log('[CONVERSION] Saving rules:', conversion_rules.value);
    const response = await $http(
      'PUT',
      '/save_asset_conversion_rules',
      JSON.stringify({ rules: conversion_rules.value }),
      undefined,
      false // Use API routing, not direct
    );
    console.log('[CONVERSION] Save response:', response);
  } catch (e) {
    console.error('[CONVERSION] Failed to save conversion rules:', e);
    throw e;
  }
}

async function clean_assets() {
  cleanAssetsLoading.value = true;
  try {
    await $http('DELETE', '/clear_expired_assets');
  } catch (e) {
    console.log('error');
  }
  cleanAssetsLoading.value = false;
}

async function save_location() {
  loadingSave.value = true;

  // Save both asset config and conversion rules
  await Promise.all([
    $http(
      'PUT',
      '/save_asset_config',
      JSON.stringify({
        location: location.value,
        asset_expire_time: asset_expire_time.value,
      })
    ),
    save_conversion_rules(),
  ]);

  editing_assets_location.value = false;
  loadingSave.value = false;
}

function resetResourceLocation(resourceLocationUUID) {
  $http(
    'PUT',
    `/reset_resource_location?uuid=${resourceLocationUUID}`
  );
}
</script>
<style scoped>
@import './config.css';

.ingest-header {
  width: max-content;
  display: flex;
}

/* Fix dark mode border on conversion rule cards */
.conversion-rule-card {
  border-color: var(--v-border-color, rgba(0, 0, 0, 0.12)) !important;
}

:root[class*='dark'] .conversion-rule-card,
.v-theme--dark .conversion-rule-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
}
</style>
